/**
 * Read the six-digit code out of the SMS, so nobody types it.
 *
 * Two mechanisms, and they are not alternatives — a phone gets whichever it
 * supports and neither needs the other:
 *
 * - **`autocomplete="one-time-code"`** on the input. iOS offers the code above
 *   the keyboard; Android suggests it. One attribute, already on every code
 *   field here, and it is what most people will actually use.
 * - **WebOTP** (this file). Chrome on Android fills the field with no tap at
 *   all, the moment the message arrives.
 *
 * ## WebOTP fires only when three things line up
 *
 * 1. The page is **HTTPS**. It is now — `scripts/dev-tls.mjs` locally.
 * 2. The message ENDS with `@<host> #<code>`, which `lib/codeMessage.ts`
 *    appends.
 * 3. **That host equals this page's host exactly.** Not the registrable
 *    domain: a code typed on `login.myrave.io` needs `@login.myrave.io`, and
 *    `@myrave.io` fills nothing. `otpBindingHost` on the server reads the host
 *    from the request that asked for the code, for precisely this reason.
 *
 * When any of them is missing **nothing happens and nothing is reported** —
 * no error, no warning. Which is why the binding is derived rather than
 * guessed, and why the attribute above is the mechanism that carries the
 * common case.
 *
 * ## It must never be load-bearing
 *
 * The field stays editable and the form stays submittable. An abort, an
 * unsupported browser, a person who pastes it themselves — all ordinary. This
 * is an accelerator on top of a thing that already works.
 */
import { useEffect } from 'react';

interface OtpCredential extends Credential {
  code: string;
}

/**
 * Fill a one-time code from the SMS, where the browser can.
 *
 * @param onCode called with the digits. Safe to pass a fresh closure — the
 *   effect keeps the latest in a ref-free way by re-subscribing, and an
 *   in-flight request is aborted on cleanup so two mounts cannot both wait.
 * @param enabled false while the field is not on screen, so the browser is not
 *   asked to watch for a message nobody is waiting for.
 */
export function useOneTimeCode(onCode: (code: string) => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    // Feature-detected, not browser-sniffed. Safari has neither and needs none.
    if (!('OTPCredential' in window) || !navigator.credentials) return;

    /*
     * The request stays open until a matching message arrives, so it MUST be
     * abortable: left running, a second mount opens a second listener and the
     * browser rejects one of them with an error nobody asked for.
     */
    const abort = new AbortController();

    navigator.credentials
      .get({
        // Not in the DOM lib's types — WebOTP is still a draft — so this is
        // the one place a cast is honest rather than lazy.
        otp: { transport: ['sms'] },
        signal: abort.signal,
      } as CredentialRequestOptions)
      .then((credential) => {
        const code = (credential as OtpCredential | null)?.code;
        if (code) onCode(code);
      })
      /*
       * Silent. An abort on unmount is the COMMON path, and a browser that
       * declines is not something to tell somebody about while they are
       * looking at a field they can simply type into.
       */
      .catch(() => undefined);

    return () => abort.abort();
  }, [onCode, enabled]);
}
